import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { validateKindaOkEmail, validateRequired, validatorFactory } from '@application/form_validators';
import { HttpError } from '@application/http_error';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { CellulaFormSubmitData, Flex, Form, InputCheckbox, InputText, NotificationAlert, Text, TextLink, toast } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { ConsentTrigger } from '@component/consent/consent_trigger.component';
import { Shimmer } from '@component/new_shimmer.component';
import { Async } from '@cortex-shim/async.component';
import { FieldOption, IdentityApiModel } from '@visiba/api-vcs-client-v2';
import { useResolve, useService } from '@visiba/instantiation';
import { useRef } from 'react';
import styled from 'styled-components';
import { MyAccountDeleteButton } from './_my_account_delete_button.component';
import { MyAccountUnauthorized } from './_my_account_unauthorized.component';
import { MyAccountController, type MyAccountFormData, MyAccountState } from './my_account.controller';

const elements = {
	container: styled.div`
		box-sizing: border-box;
		container: MyAccountContainer / inline-size;
	`,
	buttons: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x2};
		justify-content: space-between;
		margin-top: ${tokens.spacing.x2};

		@container MyAccountContainer (min-width: 568px) {
			flex-direction: row-reverse;
			border-top: 1px solid ${tokens.color.border.default};
			padding-top: ${tokens.spacing.x2};
			align-items: space-between;
			justify-content: space-between;
		}
	`,
	buttonsDivider: styled.div`
		border-top: 1px solid ${tokens.color.border.default};

		@container MyAccountContainer (min-width: 568px) {
			border: none;
		}
	`,
	notificationFieldset: styled.fieldset`
		all: unset;
	`,
	notificationFieldsetLabel: styled.legend`
		all: unset;
		font: ${tokens.text.body.smallRegular};
		margin-bottom: ${tokens.spacing.x1_5};
	`,
};

export function MyAccountForm(_: unknown): JSX.Element {
	const controller = useResolve(MyAccountController);
	const universalConfig = useResolve(UniversalConfig);
	const analyticService = useService(AnalyticService);
	const submitButtonRef = useRef<HTMLButtonElement>(null);

	async function handleSubmit(data: CellulaFormSubmitData) {
		analyticService.trackEvent(AnalyticCategories.MyAccount, 'Submit form');

		try {
			if ((await controller.save(data as Partial<MyAccountFormData>)) === false) {
				if (submitButtonRef.current) {
					submitButtonRef.current.focus();
				}

				return false;
			}

			toast({
				label: withTranslation('PW.Feature.MyAccount.SaveSuccess'),
			});

			return Ok();
		} catch (_error) {
			analyticService.trackEvent(AnalyticCategories.MyAccount, 'Submit form error');

			return Err(I18nAndL10n.translate('MyProfile.Error.Generic'));
		}
	}

	if (controller.state.value === MyAccountState.Unauthorized) {
		return <MyAccountUnauthorized />;
	}

	if (controller.state.value === MyAccountState.Error) {
		return <NotificationAlert variant='danger'>{withTranslation('PW.Feature.MyAccount.FetchError')}</NotificationAlert>;
	}

	return (
		<Async derive={controller.userData.value} fallback={<Skeleton />}>
			{(data) => {
				if (!data.identity) {
					return null;
				}

				const showNhsLoginInfo = shouldShowNhsLoginInfo(data.identity);

				return (
					<elements.container>
						<Flex gap={tokens.spacing.x5}>
							{showNhsLoginInfo ? <NhsLoginInfo remoteLoginScheme={data.identity.RemoteLoginScheme ?? undefined} /> : null}

							{!universalConfig.disableTrackingCookies ? (
								<Flex gap={tokens.spacing.x1}>
									<Text variant='labelSemiBold'>{withTranslation('PW.Feature.MyAccount.CookieTitle')}</Text>

									<ConsentTrigger />
								</Flex>
							) : null}

							<Form.Root
								options={{
									mode: 'onSubmit',
									defaultValues: {
										nin: data.identity.NationalIdentityNumber ?? '',
										firstName: data.identity.FirstName ?? '',
										lastName: data.identity.LastName ?? '',
										phone: data.identity.Phone ?? '',
										email: data.identity.Email ?? '',
										notificationsViaSms: data.notificationSettings?.AllowSms ?? false,
										notificationsViaEmail: data.notificationSettings?.AllowEmail ?? false,
									},
								}}
								onSubmit={handleSubmit}
								disabledSubmitButtonWhenClean
							>
								<Flex gap={tokens.spacing.x2}>
									<InputText label={withTranslation('PW.Label.Nin')} name='nin' readOnly required />
									<InputText
										label={withTranslation('PW.Label.FirstName')}
										readOnly={data.identity.NameRequirement === FieldOption.ReadOnly}
										name='firstName'
										required
										options={{
											validate: validatorFactory([
												(v) => validateRequired(v) || I18nAndL10n.translate('PW.FormValidation.FirstName.Required'),
											]),
										}}
									/>
									<InputText
										label={withTranslation('PW.Label.LastName')}
										name='lastName'
										readOnly={data.identity.NameRequirement === FieldOption.ReadOnly}
										required
										options={{
											validate: validatorFactory([
												data.identity.NameRequirement === FieldOption.ReadOnly
													? undefined
													: (v) => validateRequired(v) || I18nAndL10n.translate('PW.FormValidation.LastName.Required'),
											]),
										}}
									/>

									<InputText
										label={withTranslation('PW.Label.MobileNumber')}
										name='phone'
										readOnly={data.identity.PhoneRequirement === FieldOption.ReadOnly}
										required={[FieldOption.Required, FieldOption.ReadOnly].includes(data.identity.PhoneRequirement)}
										options={{
											validate: validatorFactory([
												data.identity.PhoneRequirement === FieldOption.Required
													? (v) => validateRequired(v) || I18nAndL10n.translate('PW.FormValidation.MobileNumber.Required')
													: undefined,
											]),
										}}
									/>

									<InputText
										label={withTranslation('PW.Label.EmailAddress')}
										name='email'
										readOnly={data.identity.EmailRequirement === FieldOption.ReadOnly}
										required={[FieldOption.Required, FieldOption.ReadOnly].includes(data.identity.EmailRequirement)}
										options={{
											validate: validatorFactory([
												data.identity.EmailRequirement === FieldOption.Required
													? (v) => validateRequired(v) || I18nAndL10n.translate('PW.FormValidation.Email.Required')
													: undefined,
												(v) => validateKindaOkEmail(v) || I18nAndL10n.translate('PW.FormValidation.Email.Invalid'),
											]),
										}}
									/>

									<Flex gap={tokens.spacing.x1_5}>
										<Text variant='labelSemiBold'>{withTranslation('PW.Label.Notifications')}</Text>
										{/* The wrapping div is due to the fieldset getting a 32px height FOR SOME REASON... */}
										<div>
											<elements.notificationFieldset>
												<elements.notificationFieldsetLabel>
													{withTranslation('PW.Feature.MyAccount.NotificationDescription')}
												</elements.notificationFieldsetLabel>
												<Flex gap={tokens.spacing.x1}>
													<InputCheckbox label={withTranslation('PW.Label.Sms')} name='notificationsViaSms' />
													<InputCheckbox label={withTranslation('PW.Label.Email')} name='notificationsViaEmail' />
												</Flex>
											</elements.notificationFieldset>
										</div>
									</Flex>

									<Flex>
										<Form.Error />
										<elements.buttons>
											<Form.SubmitButton ref={submitButtonRef}>{withTranslation('PW.Feature.MyAccount.SaveButton')}</Form.SubmitButton>
											<elements.buttonsDivider />
											<MyAccountDeleteButton canDelete={data.canDelete} />
										</elements.buttons>
									</Flex>
								</Flex>
							</Form.Root>
						</Flex>
					</elements.container>
				);
			}}
		</Async>
	);
}

function Skeleton(): JSX.Element {
	return (
		<Flex gap={tokens.spacing.x2}>
			<Flex gap={tokens.spacing.x0_5}>
				<Shimmer height={20} width={100} />
				<Shimmer height={48} />
			</Flex>
			<Flex gap={tokens.spacing.x0_5}>
				<Shimmer height={20} width={160} />
				<Shimmer height={48} />
			</Flex>
			<Flex gap={tokens.spacing.x0_5}>
				<Shimmer height={20} width={120} />
				<Shimmer height={48} />
			</Flex>
		</Flex>
	);
}

export function shouldShowNhsLoginInfo(identity: IdentityApiModel): boolean {
	return ['nhs', 'nhs-integration', 'nhs-sandpit'].includes(identity.RemoteLoginScheme?.toLowerCase() ?? '');
}

export function getNhsLoginUrl(remoteLoginScheme: string | undefined): string {
	switch (remoteLoginScheme?.toLowerCase()) {
		case 'nhs-integration':
			return 'https://settings.aos.signin.nhs.uk';
		case 'nhs-sandpit':
			return 'https://settings.sandpit.signin.nhs.uk';
		default:
			return 'https://settings.login.nhs.uk';
	}
}

function NhsLoginInfo({ remoteLoginScheme }: { remoteLoginScheme: string | undefined }): JSX.Element {
	const nhsLoginUrl = getNhsLoginUrl(remoteLoginScheme);

	return (
		<Flex gap={tokens.spacing.x1}>
			<Text variant='labelSemiBold'>{withTranslation('PW.Feature.MyAccount.LoginInfo.Nhs.Title')}</Text>
			<Text.p variant='bodyRegular'>{withTranslation('PW.Feature.MyAccount.LoginInfo.Nhs.Description')}</Text.p>
			<TextLink href={nhsLoginUrl} target='_blank'>
				{withTranslation('PW.Feature.MyAccount.LoginInfo.Nhs.LinkText')}
			</TextLink>
		</Flex>
	);
}
